type ClientErrorSource = "error-boundary" | "unhandled-rejection";

const MAX_FIELD_LENGTH = 900;

function trim(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized ? normalized.slice(0, MAX_FIELD_LENGTH) : undefined;
}

function errorDetails(error: unknown) {
  if (error instanceof Error) {
    return {
      name: trim(error.name) || "Error",
      message: trim(error.message) || "Unknown client error",
      stack: trim(error.stack),
    };
  }

  return {
    name: "Unhandled rejection",
    message: trim(typeof error === "string" ? error : JSON.stringify(error)) || "Unknown client error",
  };
}

/**
 * Sends only technical failure details needed to repair a public page. It never
 * includes form values, user identifiers, cookies, query parameters, or page HTML.
 */
export function reportClientError(error: unknown, source: ClientErrorSource, componentStack?: string) {
  if (typeof window === "undefined") return;

  const payload = JSON.stringify({
    source,
    route: window.location.pathname,
    userAgent: trim(navigator.userAgent),
    componentStack: trim(componentStack),
    ...errorDetails(error),
  });

  try {
    const body = new Blob([payload], { type: "application/json" });
    if (navigator.sendBeacon?.("/api/client-errors", body)) return;
    void fetch("/api/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    });
  } catch {
    // Error reporting must never introduce another visitor-facing error.
  }
}
